import React from 'react';
import { PageSpeedResult } from '../../../types';
import ReportDownloadV2 from './ReportDownloadV2';

interface LlmAnalysisSectionV2EnhancedProps {
  pageSpeedData: PageSpeedResult;
  url: string;
  llmAnalysis?: string | null;
  isLlmLoading?: boolean;
  showHallOfFame: boolean;
  setShowHallOfFame: (show: boolean) => void;
  setModalOrigin: (origin: { x: number; y: number }) => void;
  customRatings?: { mobile: number; desktop: number; overall: number };
}

// V2 디자인 토큰 시스템
const designSystem = {
  colors: {
    primary: '#2467E7',
    primaryDark: '#1B4DB8',
    primaryLight: '#E8F2FF',
    secondary: '#6C5CE7',
    accent: '#00D4FF',
    success: '#00B894',
    warning: '#FDCB6E',
    danger: '#E17055',
    gray50: '#F8FAFC',
    gray100: '#F1F5F9',
    gray200: '#E2E8F0',
    gray300: '#CBD5E1',
    gray400: '#94A3B8',
    gray500: '#64748B',
    gray600: '#475569',
    gray700: '#334155',
    gray800: '#1E293B',
    gray900: '#0F172A'
  },
  shadows: {
    sm: '0 1px 3px rgba(0, 0, 0, 0.1)',
    md: '0 4px 6px rgba(0, 0, 0, 0.07)',
    lg: '0 10px 15px rgba(0, 0, 0, 0.1)',
    xl: '0 20px 25px rgba(0, 0, 0, 0.1)'
  },
  typography: {
    h1: { fontSize: '2.25rem', fontWeight: '700', lineHeight: '1.2' },
    h2: { fontSize: '1.875rem', fontWeight: '600', lineHeight: '1.3' },
    h3: { fontSize: '1.5rem', fontWeight: '600', lineHeight: '1.4' },
    body: { fontSize: '1rem', fontWeight: '400', lineHeight: '1.5' }
  },
  spacing: {
    xs: '0.5rem',
    sm: '0.75rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem',
    '2xl': '3rem'
  }
};

const LlmAnalysisSectionV2Enhanced: React.FC<LlmAnalysisSectionV2EnhancedProps> = ({
  pageSpeedData,
  url,
  llmAnalysis,
  isLlmLoading,
  showHallOfFame,
  setShowHallOfFame,
  setModalOrigin,
  customRatings
}) => {
  // 주제별 이미지 설정
  const topicImages = {
    javascript: { src: '/JS-blocking.png', alt: 'JavaScript 최적화', fallback: '⚡' },
    http: { src: '/http-request.png', alt: 'HTTP 요청 최적화', fallback: '🌐' },
    font: { src: '/font.png', alt: '외부 폰트 사용', fallback: '🔤' },
    resource: { src: '/resources.png', alt: '리소스 관리', fallback: '📦' },
    page: { src: '/page-excessive.png', alt: '페이지 용량 최적화', fallback: '📄' },
    default: { src: '/resources.png', alt: '기타 개선사항', fallback: '🔧' }
  };

  // 키워드 기반 이미지 매핑 (우선순위 개선 - 구체적 키워드 우선)
  const getImageForContent = (content: string) => {
    const contentLower = content.toLowerCase();

    console.log('🖼️ 이미지 매칭 중:', contentLower.substring(0, 50));

    // 1순위: 폰트 (가장 구체적)
    if (contentLower.includes('폰트') || contentLower.includes('font') ||
        contentLower.includes('외부 폰트') || contentLower.includes('웹폰트')) {
      console.log('✅ 폰트 이미지 선택');
      return topicImages.font;
    }

    // 2순위: HTTP 요청 (구체적 키워드)
    if (contentLower.includes('http 요청') || contentLower.includes('요청 과다') ||
        contentLower.includes('요청 수') || contentLower.includes('http request')) {
      console.log('✅ HTTP 요청 이미지 선택');
      return topicImages.http;
    }

    // 3순위: 페이지 용량/크기
    if ((contentLower.includes('페이지') && (contentLower.includes('용량') || contentLower.includes('크기'))) ||
        contentLower.includes('페이지 과다') || contentLower.includes('용량 과다')) {
      console.log('✅ 페이지 용량 이미지 선택');
      return topicImages.page;
    }

    // 4순위: JavaScript (블로킹, 스크립트 등)
    if (contentLower.includes('javascript') || contentLower.includes('자바스크립트') ||
        contentLower.includes('js 파일') || contentLower.includes('블로킹') ||
        contentLower.includes('렌더링 차단')) {
      console.log('✅ JavaScript 이미지 선택');
      return topicImages.javascript;
    }

    // 5순위: CSS/리소스 (범용)
    if (contentLower.includes('css') || contentLower.includes('stylesheet') ||
        contentLower.includes('리소스 관리') || contentLower.includes('이미지 최적화')) {
      console.log('✅ 리소스 이미지 선택');
      return topicImages.resource;
    }

    // 6순위: 리소스 단독 (가장 범용적)
    if (contentLower.includes('리소스') || contentLower.includes('resource')) {
      console.log('✅ 리소스 이미지 선택 (범용)');
      return topicImages.resource;
    }

    console.log('⚠️ 기본 이미지 선택');
    return topicImages.default;
  };

  // V2 스타일 이미지 폴백 컴포넌트
  const ImageWithFallbackV2 = ({ src, alt, style }: { src: string; alt: string; style: any }) => {
    const [imageError, setImageError] = React.useState(false);

    if (imageError || !src) {
      return (
        <div style={{
          ...style,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: designSystem.colors.gray100,
          fontSize: '3rem',
          color: designSystem.colors.gray500
        }}>
          🔧
        </div>
      );
    }

    return (
      <img
        src={src}
        alt={alt}
        style={style}
        onError={() => setImageError(true)}
      />
    );
  };

  // 마크다운 특수문자 제거 함수
  const cleanMarkdownText = (text: string): string => {
    return text
      // 볼드/이탤릭 마크다운 제거 (*** 또는 ** 또는 *)
      .replace(/\*\*\*(.+?)\*\*\*/g, '$1')
      .replace(/\*\*(.+?)\*\*/g, '$1')
      .replace(/\*(.+?)\*/g, '$1')
      // 시작 부분 불릿 포인트 제거 (-, *, •)
      .replace(/^[-*•]\s+/, '')
      // 숫자 불릿 제거 (1., 2., 등)
      .replace(/^\d+\.\s+/, '')
      // 여러 개의 공백을 하나로
      .replace(/\s+/g, ' ')
      // 앞뒤 공백 제거
      .trim();
  };

  // 강력한 파싱 함수 - 실제 백엔드 LLM 응답 구조에 맞춘 파싱
  const parseAnalysisContent = (analysis: string) => {
    if (!analysis || typeof analysis !== 'string') {
      console.warn('parseAnalysisContent: analysis is not a string:', typeof analysis, analysis);
      return { goodPoints: [], improvementAreas: [] };
    }

    console.log('🔍 파싱할 LLM 분석 내용:', analysis.substring(0, 200) + '...');

    const lines = analysis.split('\n').map(line => line.trim()).filter(line => line.length > 0);
    const goodPoints: string[] = [];
    const improvementAreas: Array<{title: string, content: string[], image: any}> = [];

    let currentSection = 'none';
    let currentCategory = '';
    let currentCategoryContent: string[] = [];

    for (const line of lines) {
      console.log('📝 처리 중인 라인:', line);

      // 메인 섹션 구분자 감지 (### 1. ✅ **우수한 점** 형태)
      if (line.includes('✅') && (line.includes('우수한 점') || line.includes('우수한'))) {
        // 이전 카테고리 정리
        if (currentSection === 'improvement' && currentCategory && currentCategoryContent.length > 0) {
          addImprovementCategory(currentCategory, currentCategoryContent, improvementAreas);
        }
        currentSection = 'good';
        currentCategory = '';
        currentCategoryContent = [];
        console.log('✅ 우수한 점 섹션 시작');
        continue;
      } else if (line.includes('⚠️') && (line.includes('개선이 필요한') || line.includes('개선'))) {
        // 이전 good 섹션 정리
        if (currentSection === 'good' && currentCategoryContent.length > 0) {
          goodPoints.push(...currentCategoryContent);
          currentCategoryContent = [];
        }
        currentSection = 'improvement';
        currentCategory = '';
        currentCategoryContent = [];
        console.log('⚠️ 개선 필요 섹션 시작');
        continue;
      }

      // 볼드 카테고리 헤더 감지 (**JavaScript 최적화:** 형태)
      const boldHeaderMatch = line.match(/^\*\*(.+?)\*\*:?$/);
      if (boldHeaderMatch && currentSection === 'improvement') {
        // 이전 카테고리 저장
        if (currentCategory && currentCategoryContent.length > 0) {
          addImprovementCategory(currentCategory, currentCategoryContent, improvementAreas);
        }
        currentCategory = boldHeaderMatch[1];
        currentCategoryContent = [];
        console.log('🏷️ 새 카테고리:', currentCategory);
        continue;
      }

      // Markdown 헤더 스킵
      if (line.match(/^#{1,6}\s/)) {
        continue;
      }

      // 빈 불릿 포인트나 넘버링 스킵
      if (line.match(/^[\d]+\.\s*$/) || line.match(/^-\s*$/)) {
        continue;
      }

      // 실제 내용 수집
      if (line.length > 0) {
        currentCategoryContent.push(line);
      }
    }

    // 마지막 섹션/카테고리 정리
    if (currentSection === 'good' && currentCategoryContent.length > 0) {
      goodPoints.push(...currentCategoryContent);
    } else if (currentSection === 'improvement' && currentCategory && currentCategoryContent.length > 0) {
      addImprovementCategory(currentCategory, currentCategoryContent, improvementAreas);
    }

    console.log('📊 파싱 결과 - 우수한 점:', goodPoints.length, '개, 개선 영역:', improvementAreas.length, '개');
    return { goodPoints, improvementAreas };
  };

  // 개선 영역 카테고리를 추가하는 헬퍼 함수
  const addImprovementCategory = (category: string, content: string[], improvementAreas: Array<{title: string, content: string[], image: any}>) => {
    const image = getImageForContent(category + ' ' + content.join(' '));

    // 카테고리와 내용에서 마크다운 특수문자 제거
    const cleanedCategory = cleanMarkdownText(category);
    const cleanedContent = content.map(line => cleanMarkdownText(line)).filter(line => line.length > 0);

    improvementAreas.push({
      title: cleanedCategory,
      content: cleanedContent,
      image: image
    });
    console.log('📦 카테고리 추가:', cleanedCategory, '- 내용 수:', cleanedContent.length);
  };


  const { goodPoints: rawGoodPoints, improvementAreas } = parseAnalysisContent(llmAnalysis || '');

  // goodPoints 클리닝 (마크다운 특수문자 제거)
  const goodPoints = rawGoodPoints
    .map(point => cleanMarkdownText(point))
    .filter(point => point.length > 0);

  console.log('🚀 LlmAnalysisSectionV2Enhanced - Component rendered!');
  console.log('📊 Parsing results:', { goodPointsCount: goodPoints.length, improvementAreasCount: improvementAreas.length });

  // 배지 개수에 따른 동적 그리드 컬럼 계산 함수
  const getGridColumns = (count: number): string => {
    if (count === 1) return '1fr'; // 1개: 단일 열
    if (count === 2) return 'repeat(2, 1fr)'; // 2개: 2열
    if (count === 4) return 'repeat(2, 1fr)'; // 4개: 2x2 그리드
    return 'repeat(3, 1fr)'; // 3, 5, 6개: 3열 (기본)
  };

  // 배지 컨테이너 최대 너비 계산 (1~2개일 때 너무 넓게 퍼지지 않도록)
  const getMaxWidth = (count: number): string | undefined => {
    if (count === 1) return '600px'; // 1개: 최대 600px
    if (count === 2) return '900px'; // 2개: 최대 900px
    return undefined; // 3개 이상: 제한 없음
  };

  return (
    <div style={{
      background: `linear-gradient(135deg, ${designSystem.colors.gray50} 0%, ${designSystem.colors.gray100} 100%)`,
      borderRadius: '1.5rem',
      padding: designSystem.spacing['2xl'],
      marginBottom: designSystem.spacing['2xl'],
      border: `1px solid ${designSystem.colors.gray200}`,
      position: 'relative',
      boxShadow: designSystem.shadows.xl
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: designSystem.spacing.lg
      }}>
        <h5 style={{
          ...designSystem.typography.h2,
          color: designSystem.colors.gray800,
          margin: 0,
          display: 'flex',
          alignItems: 'center',
          gap: designSystem.spacing.sm,
          textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)'
        }}>
          분석 총평
        </h5>

        <div style={{ display: 'flex', gap: designSystem.spacing.sm }}>
          <ReportDownloadV2
            pageSpeedData={pageSpeedData}
            url={url}
            llmAnalysis={llmAnalysis}
            customRatings={customRatings}
          />

          <button
            id="hall-of-fame-button"
            onClick={() => {
              console.log('🔥 Hall of Fame 버튼 클릭됨!');
              console.log('현재 showHallOfFame 상태:', showHallOfFame);
              console.log('setShowHallOfFame 함수 타입:', typeof setShowHallOfFame);
              console.log('setShowHallOfFame 함수:', setShowHallOfFame);
              console.log('setModalOrigin 함수:', setModalOrigin);

              setModalOrigin({ x: 50, y: 50 });
              console.log('✅ setModalOrigin 호출 완료');

              setShowHallOfFame(!showHallOfFame);
              console.log('✅ setShowHallOfFame 호출 완료, 새로운 값:', !showHallOfFame);
            }}
            style={{
              background: `linear-gradient(135deg, ${designSystem.colors.warning} 0%, #fbbf24 100%)`,
              color: 'white',
              border: 'none',
              borderRadius: '1.5rem',
              padding: '0.875rem 1.5rem',
              fontSize: '1rem',
              fontWeight: '700',
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(253, 203, 110, 0.4)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              whiteSpace: 'nowrap',
              fontFamily: "'Pretendard', 'Noto Sans KR', -apple-system, BlinkMacSystemFont, sans-serif",
              letterSpacing: '-0.01em'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = `linear-gradient(135deg, #fbbf24 0%, ${designSystem.colors.warning} 100%)`;
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 8px 20px rgba(253, 203, 110, 0.5)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = `linear-gradient(135deg, ${designSystem.colors.warning} 0%, #fbbf24 100%)`;
              e.currentTarget.style.transform = 'translateY(0px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(253, 203, 110, 0.4)';
            }}
          >
            <span style={{
              fontSize: '1.25rem',
              lineHeight: '1'
            }}>🏆</span>
            최고 사이트와 비교하기
          </button>
        </div>
      </div>

      {/* 참고사항 박스 - 현대적인 디자인 */}
      <div style={{
        marginBottom: designSystem.spacing['2xl'],
        padding: '1.75rem 2rem',
        background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
        borderRadius: '1.5rem',
        border: `2px solid ${designSystem.colors.warning}30`,
        boxShadow: '0 8px 20px rgba(253, 203, 110, 0.1)',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '1rem'
      }}>
        <span style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '2.5rem',
          height: '2.5rem',
          borderRadius: '50%',
          background: `linear-gradient(135deg, ${designSystem.colors.warning} 0%, #fbbf24 100%)`,
          color: 'white',
          fontSize: '1.25rem',
          flexShrink: 0,
          boxShadow: '0 4px 12px rgba(253, 203, 110, 0.3)'
        }}>💡</span>
        <div style={{ flex: 1 }}>
          <h6 style={{
            margin: '0 0 0.5rem 0',
            fontSize: '1.125rem',
            fontWeight: '700',
            color: '#92400e',
            letterSpacing: '-0.01em'
          }}>
            참고사항
          </h6>
          <p style={{
            margin: 0,
            fontSize: '1.05rem',
            color: '#92400e',
            lineHeight: '1.65',
            fontWeight: '500',
            fontFamily: "'Pretendard', 'Noto Sans KR', -apple-system, BlinkMacSystemFont, sans-serif"
          }}>
            웹사이트 성능 최적화 권장 기준: 페이지 용량 <strong>35MB 이하</strong>(PC 기준), HTTP 요청 수 <strong>50~60회 이하</strong>를 권장합니다.
          </p>
        </div>
      </div>

      {isLlmLoading ? (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: designSystem.spacing.sm,
          padding: designSystem.spacing.lg,
          backgroundColor: designSystem.colors.primaryLight,
          borderRadius: '0.75rem',
          border: `2px solid ${designSystem.colors.primary}`
        }}>
          <div style={{
            width: '20px',
            height: '20px',
            border: `2px solid ${designSystem.colors.primary}`,
            borderTop: '2px solid transparent',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          <span style={{ color: designSystem.colors.primary, fontWeight: '600' }}>
            🤖 AI가 리소스를 분석하고 전문 보고서를 작성중입니다...
          </span>
        </div>
      ) : llmAnalysis && typeof llmAnalysis === 'string' ? (
        <div>
          {/* 우수한 점 섹션 - 현대적인 3열 배지 그리드 */}
          {goodPoints.length > 0 && (
            <div style={{
              marginBottom: designSystem.spacing['2xl'],
              padding: '2.5rem',
              background: 'linear-gradient(135deg, #ffffff 0%, #f0fdf4 100%)',
              borderRadius: '1.5rem',
              border: `2px solid ${designSystem.colors.success}30`,
              boxShadow: '0 8px 20px rgba(0, 184, 148, 0.1)'
            }}>
              <h4 style={{
                margin: `0 0 1.75rem 0`,
                color: designSystem.colors.success,
                ...designSystem.typography.h3,
                fontSize: '1.75rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem'
              }}>
                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '2.5rem',
                  height: '2.5rem',
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${designSystem.colors.success} 0%, #00d4aa 100%)`,
                  color: 'white',
                  fontSize: '1.25rem'
                }}>✓</span>
                우수한 점
              </h4>
              <div style={{
                display: 'grid',
                gridTemplateColumns: getGridColumns(goodPoints.length),
                gap: '1.25rem',
                maxWidth: getMaxWidth(goodPoints.length),
                margin: goodPoints.length <= 2 ? '0 auto' : '0'
              }}>
                {goodPoints.map((point, index) => (
                  <div key={index} style={{
                    padding: '1.25rem 1.5rem',
                    borderRadius: '1.25rem',
                    background: 'linear-gradient(135deg, rgba(0, 184, 148, 0.08) 0%, rgba(0, 212, 170, 0.12) 100%)',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.75rem',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    border: `2px solid ${designSystem.colors.success}20`,
                    cursor: 'default',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px) scale(1.02)';
                    e.currentTarget.style.boxShadow = '0 12px 24px rgba(0, 184, 148, 0.15)';
                    e.currentTarget.style.borderColor = designSystem.colors.success;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0) scale(1)';
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.borderColor = `${designSystem.colors.success}20`;
                  }}>
                    {/* 배경 장식 */}
                    <div style={{
                      position: 'absolute',
                      top: '-20px',
                      right: '-20px',
                      width: '60px',
                      height: '60px',
                      borderRadius: '50%',
                      background: `radial-gradient(circle, ${designSystem.colors.success}15 0%, transparent 70%)`,
                      pointerEvents: 'none'
                    }} />

                    <span style={{
                      color: designSystem.colors.success,
                      fontWeight: '800',
                      fontSize: '1.5rem',
                      flexShrink: 0,
                      lineHeight: '1'
                    }}>✓</span>
                    <span style={{
                      ...designSystem.typography.body,
                      color: designSystem.colors.gray800,
                      lineHeight: '1.7',
                      fontSize: '0.95rem',
                      fontWeight: '500',
                      letterSpacing: '0.01em',
                      wordBreak: 'keep-all',
                      fontFamily: "'Pretendard', 'Noto Sans KR', -apple-system, BlinkMacSystemFont, sans-serif"
                    }}>
                      {point}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 개선이 필요한 영역 - 현대적인 카드 형태 (이미지 상단) */}
          {improvementAreas.length > 0 && (
            <div style={{
              marginBottom: designSystem.spacing['2xl'],
              padding: '2.5rem',
              background: 'linear-gradient(135deg, #ffffff 0%, #fffbeb 100%)',
              borderRadius: '1.5rem',
              border: `2px solid ${designSystem.colors.warning}30`,
              boxShadow: '0 8px 20px rgba(253, 203, 110, 0.1)'
            }}>
              <h4 style={{
                margin: `0 0 1.75rem 0`,
                color: designSystem.colors.warning,
                ...designSystem.typography.h3,
                fontSize: '1.75rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem'
              }}>
                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '2.5rem',
                  height: '2.5rem',
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${designSystem.colors.warning} 0%, #fbbf24 100%)`,
                  color: 'white',
                  fontSize: '1.25rem'
                }}>⚠</span>
                개선이 필요한 영역
              </h4>

              <div style={{
                display: 'grid',
                gap: '1.5rem',
                gridTemplateColumns: getGridColumns(improvementAreas.length),
                maxWidth: getMaxWidth(improvementAreas.length),
                margin: improvementAreas.length <= 2 ? '0 auto' : '0'
              }}>
                {improvementAreas.map((area, index) => (
                  <div key={index} style={{
                    display: 'flex',
                    flexDirection: 'column',
                    padding: '0',
                    background: 'linear-gradient(135deg, rgba(253, 203, 110, 0.08) 0%, rgba(251, 191, 36, 0.12) 100%)',
                    borderRadius: '1.5rem',
                    border: `2px solid ${designSystem.colors.warning}20`,
                    overflow: 'hidden',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: 'default',
                    position: 'relative'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-6px)';
                    e.currentTarget.style.boxShadow = '0 16px 32px rgba(253, 203, 110, 0.2)';
                    e.currentTarget.style.borderColor = designSystem.colors.warning;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0px)';
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.borderColor = `${designSystem.colors.warning}20`;
                  }}>
                    {/* 이미지 영역 - 상단 전체 너비 */}
                    <div style={{
                      width: '100%',
                      height: '280px',
                      overflow: 'hidden',
                      position: 'relative',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: `linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)`
                    }}>
                      <ImageWithFallbackV2
                        src={area.image.src}
                        alt={area.image.alt}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          transform: 'scale(0.9)',
                          transition: 'transform 0.3s ease'
                        }}
                      />
                      {/* 이미지 오버레이 그라데이션 */}
                      <div style={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: '40%',
                        background: 'linear-gradient(to top, rgba(255, 255, 255, 0.85) 0%, transparent 100%)',
                        pointerEvents: 'none'
                      }} />
                    </div>

                    {/* 텍스트 콘텐츠 영역 - 하단 */}
                    <div style={{ padding: '1.5rem' }}>
                      <h5 style={{
                        margin: `0 0 1rem 0`,
                        color: designSystem.colors.gray900,
                        fontSize: '1.125rem',
                        fontWeight: '700',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        letterSpacing: '-0.01em',
                        fontFamily: "'Pretendard', 'Noto Sans KR', -apple-system, BlinkMacSystemFont, sans-serif"
                      }}>
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '1.75rem',
                          height: '1.75rem',
                          borderRadius: '50%',
                          background: `linear-gradient(135deg, ${designSystem.colors.warning} 0%, #fbbf24 100%)`,
                          color: 'white',
                          fontSize: '0.875rem'
                        }}>🔧</span>
                        {area.title}
                      </h5>

                      <div style={{
                        ...designSystem.typography.body,
                        lineHeight: '1.75',
                        color: designSystem.colors.gray700,
                        fontSize: '0.9rem',
                        fontFamily: "'Pretendard', 'Noto Sans KR', -apple-system, BlinkMacSystemFont, sans-serif"
                      }}>
                        {area.content.map((line, lineIndex) => (
                          <div key={lineIndex} style={{
                            marginBottom: '0.65rem',
                            paddingLeft: '1.25rem',
                            position: 'relative',
                            wordBreak: 'keep-all'
                          }}>
                            <span style={{
                              position: 'absolute',
                              left: '0',
                              color: designSystem.colors.warning,
                              fontWeight: '800',
                              fontSize: '1.1rem'
                            }}>•</span>
                            <span style={{
                              fontWeight: '500',
                              letterSpacing: '0.01em'
                            }}>
                              {line}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div style={{
          padding: designSystem.spacing.lg,
          backgroundColor: '#fef3c7',
          borderRadius: '0.75rem',
          border: `2px solid ${designSystem.colors.warning}`
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: designSystem.spacing.sm,
            marginBottom: designSystem.spacing.sm
          }}>
            <span style={{ fontSize: '1.5rem' }}>⚠️</span>
            <h4 style={{
              margin: 0,
              ...designSystem.typography.body,
              fontWeight: '600',
              color: '#92400e'
            }}>
              AI 분석 결과를 가져오지 못했습니다
            </h4>
          </div>
          <p style={{
            margin: 0,
            fontSize: '0.875rem',
            color: '#78350f',
            lineHeight: '1.6'
          }}>
            AI 분석 서비스에 연결할 수 없어 기본 분석을 표시합니다.
          </p>
        </div>
      )}
    </div>
  );
};

export default LlmAnalysisSectionV2Enhanced;